import { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";

import { useMediaSelector } from "./MediaSelector";

// LocalStorage key for saved CMS assets
const CMS_ASSETS_KEY = 'touchtour_cms_assets';

// Utility functions for localStorage
const getSavedCMSAssets = () => {
  try {
    const saved = localStorage.getItem(CMS_ASSETS_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.error('Error loading saved CMS assets:', error);
    return [];
  }
};

const saveCMSAsset = (asset) => {
  try {
    const saved = getSavedCMSAssets();
    // Check if asset already exists (avoid duplicates)
    const exists = saved.find(item => item.id === asset.id);
    if (!exists) {
      const updatedAssets = [...saved, asset];
      localStorage.setItem(CMS_ASSETS_KEY, JSON.stringify(updatedAssets));
    }
  } catch (error) {
    console.error('Error saving CMS asset:', error);
  }
};

const removeCMSAsset = (assetId) => {
  try {
    const saved = getSavedCMSAssets();
    const filtered = saved.filter(item => item.id !== assetId);
    localStorage.setItem(CMS_ASSETS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing CMS asset:', error);
  }
};

const asset = x => `${window.location.origin}/tour/assets/hotspots/${x}`;

export const defaultIcons = [
  {
    id: 1,
    title: "Note",
    url: "info.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 2,
    title: "Note Plus",
    url: "info-plus.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 3,
    title: "Tag",
    url: "note.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 4,
    title: "Photo",
    url: "gallery.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 5,
    title: "Link",
    url: "web-link.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 6,
    title: "Video",
    url: "video.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 7,
    title: "Audio",
    url: "audio.png",
    tags: ["icon"],
    default: true,
  },
  // {
  //   id: 8,
  //   title: "Look At",
  //   url: "poi.png",
  //   tags: ["icon"],
  //   default: true,
  // },
  {
    id: 9,
    title: "Timeline",
    url: "timeline.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 10,
    title: "Direction",
    url: "direction.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 11,
    title: "Crossfade",
    url: "crossfade.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 12,
    title: "Elevate Up",
    url: "elevate-up.png",
    tags: ["icon"],
    default: true,
  },
  {
    id: 13,
    title: "Elevate Down",
    url: "elevate-down.png",
    tags: ["icon"],
    default: true,
  },
];

const useStyles = createUseStyles({
  container: {
    position: "relative",
  },
  selector: {
    width: "100%",
    display: "flex",
    "flex-direction": "row",
    "margin-top": 15,
    height: 52,
    "background-color": props => (props.open ? "#252525" : "#313131"),
    "border-radius": [3, 3, 0, 0],
    transition: "background 0.2s ease",
  },
  image: {
    width: 40,
    height: 40,
    border: "1px solid #707070",
    "border-radius": 3,
    "margin-right": 10,
    "background-size": "cover",
  },
  largeImage: {
    extend: "image",
    width: 50,
    height: 50,
  },
  name: {
    margin: [2, 0],
  },
  button: {
    "background-color": props => (props.open ? "#313131" : "#252525"),
    border: "none",
    color: "white",
    "border-radius": 5,
    cursor: "pointer",
    padding: [5, 15],
    transition: "background 0.2s ease",
  },
  options: {
    position: "absolute",
    top: 52,
    "background-color": "#252525",
    "border-radius": [0, 0, 5, 5],
    width: "100%",
    "max-height": props => (props.open ? 250 : 0),
    "overflow-y": props => (props.open ? "scroll" : "hidden"),
    display: "flex",
    "flex-direction": "column",
    transition: "max-height 0.2s ease",
    "z-index": 50,
  },
  option: {
    width: "100%",
    display: "flex",
    "flex-direction": "row",
    "margin-bottom": 5,
    padding: 5,
    "box-sizing": "border-box",
    height: 52,
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      "background-color": "#373737",
    },
  },
  optName: {
    extend: "name",
    "margin-top": 10,
  },
  cms: {
    "text-align": "center",
    margin: [10, 0],
    width: "100%",
  },
  cmsAssetOption: {
    position: "relative",
    width: "100%",
    display: "flex",
    "flex-direction": "row",
    "margin-bottom": 5,
    padding: 5,
    "box-sizing": "border-box",
    height: 52,
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      "background-color": "#373737",
      "& $deleteButton": {
        opacity: 1,
      },
    },
  },
  deleteButton: {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: 20,
    height: 20,
    "background-color": "#ff4444",
    border: "none",
    "border-radius": "50%",
    color: "white",
    cursor: "pointer",
    opacity: 0,
    transition: "opacity 0.2s ease",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    fontSize: 12,
    "&:hover": {
      "background-color": "#ff6666",
    },
  },
});

const IconSelector = ({ value = {}, defaultIcon, onChange, set, template }) => {
  const [media, getMedia, setMedia] = useMediaSelector();
  const [open, setOpen] = useState(false);
  const [savedCMSAssets, setSavedCMSAssets] = useState([]);
  const classes = useStyles({ open });

  // Load saved CMS assets on component mount
  useEffect(() => {
    setSavedCMSAssets(getSavedCMSAssets());
  }, []);

  // Handle deleting a CMS asset
  const handleDeleteCMSAsset = (assetId, event) => {
    event.stopPropagation(); // Prevent triggering the option click
    removeCMSAsset(assetId);
    setSavedCMSAssets(getSavedCMSAssets());
  };

  if (!media.url) media.url = media.public_url;
  const tags = (media.tags ?? []).map(t => t.title);

  useEffect(() => {
    if (media.id && media.id !== value.id) {
      setOpen(false);
      if (media.default) {
        onChange({
          id: media.id,
          title: media.title,
          url: media.url,
          tags,
        });
      } else {
        // Save CMS asset to localStorage and update state
        const cmsAsset = {
          id: media.id,
          title: media.title,
          preview_url: media.preview_url,
          public_url: media.public_url,
          tags,
          fromCMS: true,
        };
        saveCMSAsset(cmsAsset);
        setSavedCMSAssets(getSavedCMSAssets());

        onChange({
          id: media.id,
          title: media.title,
          preview_url: media.preview_url,
          tags,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.id, media.id, tags, onChange]);

  const defaultOpt = defaultIcons.find(i => i.title === defaultIcon);

  useEffect(() => {
    if (!value.id) {
      onChange({
        id: defaultOpt.id,
        title: defaultOpt.title,
        url: defaultOpt.url,
        tags: defaultOpt.tags,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.id, defaultOpt.id, onChange]);

  return (
    <div className={classes.container}>
      <div className={classes.selector}>
        <div
          className={classes.largeImage}
          style={{
            backgroundImage: `url("${value.preview_url || asset(value.url)}")`,
          }}
        />

        <div className={classes.text}>
          <p className={classes.name}>{value.title ?? "Select an icon"}</p>
          {
            <button
              className={classes.button}
              onClick={() =>
                void (set.length > 1
                  ? setOpen(o => !o)
                  : getMedia([1], ["icon"]))
              }
            >
              Change icon
            </button>
          }
        </div>
      </div>
      <div className={classes.options}>
        {defaultIcons
          .filter(i => set.includes(i.title))
          .map(opt => (
            <div
              className={classes.option}
              key={opt.id}
              onClick={() => setMedia(opt)}
            >
              <div
                className={classes.image}
                style={{ backgroundImage: `url("${asset(opt.url)}")` }}
              />
              <p className={classes.optName}>{opt.title}</p>
            </div>
          ))}
        {savedCMSAssets.map(asset => (
          <div
            className={classes.cmsAssetOption}
            key={`cms-${asset.id}`}
            onClick={() => setMedia(asset)}
          >
            <div
              className={classes.image}
              style={{
                backgroundImage: `url("${asset.preview_url || asset.public_url}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            <p className={classes.optName}>{asset.title || 'CMS Asset'}</p>
            <button
              className={classes.deleteButton}
              onClick={(e) => handleDeleteCMSAsset(asset.id, e)}
              title="Remove asset"
            >
              ×
            </button>
          </div>
        ))}
        <div className={classes.option} onClick={() => getMedia([1], ["icon"])}>
          <div className={classes.cms}>Select asset from CMS</div>
        </div>
      </div>
    </div>
  );
};

export default IconSelector;
